import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useContext } from 'react';
import { AmplitudeContext } from '@/contexts/amplitude';

interface EventProperties {
  [key: string]: string | number | boolean | object;
}

interface TrackSectionViewOptions {
  eventName?: string;
  eventProperties?: EventProperties;
  threshold?: number;
  triggerOnce?: boolean;
  debounceMs?: number;
  sectionReference?: string;
}

export function useTrackSectionView({
  eventName = 'section viewed',
  eventProperties = {},
  threshold = 0.5,
  triggerOnce = true,
  debounceMs = 1000,
  sectionReference,
}: TrackSectionViewOptions = {}) {
  const { analytics } = useContext(AmplitudeContext);
  const { ref, inView } = useInView({
    threshold,
    triggerOnce,
  });

  const lastTriggeredRef = useRef<number>(0);
  const hasTrackedRef = useRef<boolean>(false);

  useEffect(() => {
    if (!analytics || (hasTrackedRef.current && triggerOnce)) {
      return;
    }

    const now = Date.now();

    if (
      inView &&
      now - lastTriggeredRef.current > debounceMs &&
      !hasTrackedRef.current
    ) {
      lastTriggeredRef.current = now;
      const properties = {
        ...eventProperties,
        name: eventName,
        description:
          'Evento disparado quando o usuário visualiza uma seção específica da página',
        section_reference: sectionReference || 'Seção genérica',
      };

      analytics.track({
        event_type: eventName,
        event_properties: properties,
      });

      if (triggerOnce) {
        hasTrackedRef.current = true;
      }
    }
  }, [
    inView,
    analytics,
    eventName,
    eventProperties,
    debounceMs,
    triggerOnce,
    sectionReference,
  ]);

  return ref;
}
